import { AxiosError } from 'axios';
import { PageProps, graphql } from 'gatsby';
import AnalyticsPoint from 'gatsby-plugin-purina-analytics/AnalyticsPoint';
import React from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CMS_THEME } from 'src/common/enums';
import { ContentBlock } from 'src/components/ContentBlock/ContentBlock';
import { CategoryTag } from 'src/components/category_tag/CategoryTag';
import { getArticleSearchData } from 'src/ssr/getSearchData';
import { getIsPaginated } from 'src/utils/helpers';
import Layout from '../../components/Layout';
import Seo from '../../components/Seo';
import {
  getHelmetPropsFromMetatagProps,
  getJsonFromMetatagProps,
} from '../../utils/metatagHelpers';
import ArticleListingBody from '../components/ArticleListingBody';
import { ArticleSD } from './types/types';

interface DataProps {
  node: IArticleListing;
  translations: Translations;
}

const ArticleListingTemplate: React.FC<PageProps<DataProps, any, any, ArticleSD>> = function ({
  data: { node, translations },
  serverData,
}) {
  const { relatedListings } = node.relationships;
  const helmetProps = getHelmetPropsFromMetatagProps(node.metatag);
  const schemaMarkup = getJsonFromMetatagProps(node.metatag);
  const { t } = useTranslation();
  return (
    <Layout
      node={node}
      metaData={node.metatag}
      language={node.langcode}
      translations={translations}
    >
      <Seo
        isPaginated={getIsPaginated(serverData.articleData)}
        {...helmetProps}
        schemaMarkup={schemaMarkup}
      />
      <Container fluid className="pds-bg-surface-layer pds-py-4 md:pds-py-6">
        <Container>
          <ContentBlock
            theme={CMS_THEME.NEUTRAL}
            textAlign={{
              xs: 'left',
              md: 'center',
            }}
            title={{
              element: 'h1',
              children: node.title,
              className: 'pds-text-title-lg md:pds-text-display-sm',
            }}
            body={
              node.body?.processed
                ? {
                    children: node.body.processed,
                    className: 'md:pds-mt-4',
                  }
                : undefined
            }
          />
        </Container>
      </Container>
      {relatedListings?.length > 0 && (
        <AnalyticsPoint
          type="module"
          label="Article subcategories"
          typeLabel="article_categories"
          as={Container}
          role="region"
          aria-label={`Categories related to ${node.title}`}
          className="pds-px-0 pds-py-5"
          fluid
        >
          <Container className="text-sm-start pds-mb-3">
            <h2 className="pds-text-left pds-text-title-sm md:pds-text-center">
              {t('Related Categories')}
            </h2>
          </Container>
          <Container className="pds-flex pds-flex-wrap pds-gap-4 md:pds-flex-row md:pds-justify-center">
            {relatedListings.map((listing: any, i: number) => {
              return (
                <AnalyticsPoint
                  key={i}
                  type="component"
                  node={{ ...listing, type: 'article_category' }}
                  className="pds-w-fit"
                >
                  <CategoryTag
                    url={listing.path.alias}
                    name={listing.relationships?.field_tags[0]?.name}
                  />
                </AnalyticsPoint>
              );
            })}
          </Container>
        </AnalyticsPoint>
      )}
      <AnalyticsPoint
        type="module"
        label="Articles section"
        typeLabel="article_listing"
        as={Container}
        fluid
      >
        <ArticleListingBody
          data={serverData.articleData}
          serverData={serverData.articleData}
          error={serverData.error}
          contextualFilters={serverData.contextualFilters}
          isFetching={false}
          defaultLoadedState
        />
      </AnalyticsPoint>
    </Layout>
  );
};

export const getServerData = async (props: any) => {
  try {
    const articleData = await getArticleSearchData(props.pageContext, props.query);
    return {
      status: 200,
      headers: {
        'Cache-Control': 'public, max-age=3600, s-maxage=3600, stale-while-revalidate=3600',
      },
      props: {
        articleData: articleData.data,
        contextualFilters: articleData.contextualFilters,
        error: null,
      },
    };
  } catch (e) {
    return {
      status: 500,
      props: {
        error: (e as AxiosError).response,
        articleData: null,
      },
    };
  }
};

export const query = graphql`
  query ($id: String!, $language: String!, $nid: Int!) {
    node: nodeArticleListingPage(id: { eq: $id }, langcode: { eq: $language }) {
      internal {
        type
      }
      id
      title
      path {
        alias
      }
      body {
        processed
      }
      langcode
      ...Metatag
      behaviors {
        page_layout {
          elements_to_hide {
            header
            footer
            chat
          }
        }
        hide_elements {
          third_party_elements_to_hide {
            one_trust
          }
        }
      }
      country_links: other_country_external_links {
        text
        url
        hreflang
      }
      relationships {
        featuredArticle: field_article {
          ...FeaturedArticleCard
        }
        species: field_species_term {
          drupal_internal__tid
        }
        tags: field_tags {
          drupal_internal__tid
        }
        relatedListings: field_related_listings {
          id
          title
          path {
            alias
          }
          langcode
          relationships {
            field_tags {
              name
            }
          }
        }
      }
    }
    translations: allNodeArticleListingPage(
      filter: { drupal_internal__nid: { eq: $nid }, langcode: { ne: $language } }
    ) {
      edges {
        node {
          langcode
          path {
            alias
          }
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

export default ArticleListingTemplate;
